"use client";

import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useArtistMovies } from "@/helpers/hooks/useArtistMovies";
import { Spinner } from "@nextui-org/react";
import Cards from "@/components/content/cards";
import { getArtistImageURL } from "@/helpers/artist.util";
import { GuideType } from "@/helpers/nextPlayGuideHelper";

const Container = styled.div`
  height: auto;
  min-height: 100vh;
`;

const ArtistName = styled.h4`
  color: #fafafa;
  font-size: 32px;
  font-weight: 600;
`;

const ArtistDescription = styled.p`
  margin-top: 10px;
  font-size: 20px;
  line-height: 1.55;
  color: #d9d9d9;
  font-weight: 400;
  overflow: hidden;
  max-height: ${({ $isExpanded }) => ($isExpanded ? "none" : "3.875em")};
`;

const DescriptionButton = styled.button`
  align-self: flex-start;
  margin-top: 8px;
  color: #d00e0e;
  font-size: 16px;
  font-weight: 500;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
`;

const ArtistDetail = ({ artist = {}, movies }) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [canToggleDescription, setCanToggleDescription] = useState(false);
  const descriptionRef = useRef(null);
  const { items, isLoading } = useArtistMovies({
    artistName: artist?.farsiName,
    initialData: movies,
    initialPage: 2,
  });

  useEffect(() => {
    const descriptionElement = descriptionRef.current;

    if (!descriptionElement) {
      return;
    }

    setIsDescriptionExpanded(false);

    const checkDescriptionOverflow = () => {
      const lineHeight = parseFloat(
        window.getComputedStyle(descriptionElement).lineHeight
      );
      const collapsedHeight = lineHeight * 2.5;

      setCanToggleDescription(
        descriptionElement.scrollHeight > collapsedHeight + 1
      );
    };

    checkDescriptionOverflow();
    window.addEventListener("resize", checkDescriptionOverflow);

    return () => {
      window.removeEventListener("resize", checkDescriptionOverflow);
    };
  }, [artist?.description]);

  return (
    <Container>
      <div className="w-full flex flex-col md:flex-col gap-5 items-center justify-center md:items-end md:container md:mx-auto pt-44 ">
        <img
          className="w-[250px] aspect-square border-2 border-zinc-700 rounded-[20px] object-cover"
          src={getArtistImageURL(artist?.image)}
          alt={artist?.farsiName || artist?.englishName}
        />
        <div className="lg:h-full text-primary-white w-full md:container mx-auto relative z-99 mb-2 px-3 mt-5 md:mt-0 md:px-0">
          <div className="flex flex-row z-10 py-1 md:py-0 mb-4">
            <ArtistName>{artist?.farsiName}</ArtistName>
          </div>
          <span className="text-[#F0F0F0] font-light md:font-medium text-sm lg:text-xl z-10 mb-6">
            {artist?.englishName}
          </span>
          {artist?.description && artist.description != "undefined" && (
            <div className="flex flex-col text-[#D9D9D9] z-10 py-2 md:py-0">
              <ArtistDescription
                ref={descriptionRef}
                $isExpanded={isDescriptionExpanded}
              >
                {artist?.description}
              </ArtistDescription>
              {canToggleDescription && (
                <DescriptionButton
                  type="button"
                  onClick={() =>
                    setIsDescriptionExpanded((isExpanded) => !isExpanded)
                  }
                >
                  {isDescriptionExpanded ? "کمتر" : "بیشتر"}
                </DescriptionButton>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="my-24 md:container mx-auto">
        <Cards
          items={items}
          isArtist={false}
          noTag={false}
          isMusic={false}
          showTitle={true}
          categoryName={""}
          type="movie"
          nextPlayGuide={{
            type: GuideType.ARTIST,
            slug: artist?.slug,
            name: artist?.farsiName,
          }}
        />
        {isLoading && (
          <div className="w-full flex justify-center items-center">
            <Spinner color="primary" />
          </div>
        )}
      </div>
    </Container>
  );
};

export default ArtistDetail;
